import type { ReplaceTargetsBody, RecipientStatus } from "@/api/campaigns.service";

// A single descriptor of "what the operator wants to freeze into a segment".
// Two modes mirror the PUT-targets contract:
//  - explicit: exactly these emails (today's behaviour);
//  - filter:   every recipient matching the current projection filter, minus a
//              handful the operator deselected after a "select all matching" —
//              so a 100k+ segment never round-trips an email list.
export type RecipientSelection =
  | { mode: "explicit"; emails: string[] }
  | {
      mode: "filter";
      // Count of recipients matching the current filter (result.total).
      total: number;
      firmRef?: string;
      regionId?: number;
      // Display-only: names resolved from the projection facets for the summary
      // chips. NOT sent to the API — the PUT-targets body carries ids only.
      regionName?: string | null;
      firmName?: string | null;
      status?: RecipientStatus;
      // Emails deselected after "select all matching" was toggled on.
      excludeEmails: string[];
    };

export const EMPTY_SELECTION: RecipientSelection = { mode: "explicit", emails: [] };

// How many recipients the selection resolves to:
//  - explicit → the chosen emails;
//  - filter   → all matching (total) minus the deselected ones.
export function selectionCount(sel: RecipientSelection): number {
  return sel.mode === "explicit"
    ? sel.emails.length
    : Math.max(sel.total - sel.excludeEmails.length, 0);
}

// Builds the PUT-targets body, omitting undefined/empty optional fields so a
// filter with no active filters/exclusions sends a minimal `{ mode: "filter" }`.
export function buildReplaceTargetsBody(sel: RecipientSelection): ReplaceTargetsBody {
  if (sel.mode === "explicit") {
    return { mode: "explicit", emails: sel.emails };
  }
  // Display-only regionName/firmName are intentionally omitted here — the API
  // body carries ids/status/excludeEmails only.
  return {
    mode: "filter",
    ...(sel.firmRef ? { firmRef: sel.firmRef } : {}),
    ...(sel.regionId !== undefined ? { regionId: sel.regionId } : {}),
    ...(sel.status !== undefined ? { status: sel.status } : {}),
    ...(sel.excludeEmails.length > 0 ? { excludeEmails: sel.excludeEmails } : {}),
  };
}
